const STATE_KEY = "axiohire_apple_oauth_state";
const RETURN_KEY = "axiohire_apple_oauth_return";

export interface ApplePublicConfig {
  apple_client_id: string | null;
  apple_enabled: boolean;
  apple_redirect_uri: string | null;
}

function randomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function startAppleRedirect(config: ApplePublicConfig, returnPath: string): void {
  const clientId = config.apple_client_id?.trim();
  const redirectUri = config.apple_redirect_uri?.trim();
  if (!config.apple_enabled || !clientId || !redirectUri) {
    throw new Error("Sign in with Apple is not configured");
  }
  const state = randomState();
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_KEY, returnPath);
  const authorize = new URL("https://appleid.apple.com/auth/authorize");
  authorize.searchParams.set("client_id", clientId);
  authorize.searchParams.set("redirect_uri", redirectUri);
  authorize.searchParams.set("response_type", "code id_token");
  authorize.searchParams.set("response_mode", "form_post");
  authorize.searchParams.set("scope", "name email");
  authorize.searchParams.set("state", state);
  window.location.assign(authorize.toString());
}

export function consumeAppleCallbackParams(search: string): {
  idToken: string;
  authorizationCode?: string;
  fullName?: string;
  returnPath: string;
} {
  const params = new URLSearchParams(search);
  const error = params.get("error");
  if (error) {
    throw new Error(error === "user_cancelled_authorize" ? "Sign in with Apple was cancelled." : error);
  }
  const expected = sessionStorage.getItem(STATE_KEY);
  const state = params.get("state");
  sessionStorage.removeItem(STATE_KEY);
  if (!expected || !state || expected !== state) {
    throw new Error("Apple sign-in could not be verified. Try again.");
  }
  const idToken = params.get("id_token");
  if (!idToken) {
    throw new Error("Apple did not return an identity token.");
  }
  const returnPath = sessionStorage.getItem(RETURN_KEY) || "/join";
  sessionStorage.removeItem(RETURN_KEY);
  return {
    idToken,
    authorizationCode: params.get("code") || undefined,
    fullName: parseAppleFullName(params.get("user")),
    returnPath,
  };
}

function parseAppleFullName(raw: string | null): string | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as {
      name?: { firstName?: string; lastName?: string };
    };
    const parts = [parsed.name?.firstName, parsed.name?.lastName].filter(Boolean);
    return parts.length ? parts.join(" ") : undefined;
  } catch {
    return undefined;
  }
}
